// @flow strict
import 'isomorphic-fetch';
import { withScope, captureException } from '@sentry/browser';
import config from '../config';

// const NOT_MODIFIED = 304;
const BAD_REQUEST = 400;
const UNAUTHORISED = 401;
// const FORBIDDEN = 403;
const REQUEST_TIMEOUT = 408;
// const CONFLICT = 409;
// const GONE = 410;

type ApiRequest = {
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: any,
};

type ApiResponse = Response & {
  isRedirected?: boolean,
  redirectLocation?: string,
};

function wait(ms = 1500) {
  const timeInMs = ms;
  return new Promise((resolve) => {
    window.setTimeout(resolve, timeInMs);
  });
}

function captureError({ status, url, method, isWarning, error }: any) {
  withScope((scope) => {
    let e;

    if (error) {
      e = error;
    } else {
      const name = `API Error: ${status}`;
      e = new Error(name);
      e.name = name;
    }

    scope.setExtra('url', url);
    scope.setExtra('method', method);

    if (status) {
      scope.setExtra('status', status);
    }

    if (isWarning) {
      scope.setLevel('warning');
    }

    captureException(e);
  });
}

function buildFetchWithRetry(url, req) {
  let retryCount = 3;

  function invoke(resolve, reject) {
    const twentySeconds = 20 * 1000;
    const timeoutMs = twentySeconds;
    const hasRetries = retryCount > 0;
    // eslint-disable-next-line compat/compat
    const controller = window.AbortController ? new window.AbortController() : undefined; // Not supported in IE11
    const { signal } = controller || { signal: undefined };
    const fetched = fetch(url, { ...req, signal });

    fetched
      .then((response) => {
        const isRetryableStatus = response.status === 503;

        if (response && isRetryableStatus && hasRetries) {
          retryCount -= 1;
          wait().then(() => invoke(resolve, reject));
          return;
        }
        resolve(response);
      })
      .catch((error) => {
        if (hasRetries) {
          retryCount -= 1;
          wait().then(() => invoke(resolve, reject));
          return;
        }
        reject(error);
      });

    const timeout = wait(timeoutMs).then(() => Promise.resolve({ status: REQUEST_TIMEOUT })); // mimic server request timeout

    // TODO: MB-5270 Reinstate when a reasonable timeout is understood and needed
    // if (controller) {
    //   timeout.then(() => {
    //     controller.abort();
    //   });
    // }

    Promise.race([fetched, timeout]).then((response) => {
      const isTimeoutCode = response.status === REQUEST_TIMEOUT;

      if (isTimeoutCode) {
        captureError({
          url,
          method: req.method,
          error: new Error(`API Error: Exceeded timeout of ${timeoutMs}`),
          isWarning: true,
        });
      }
    });
  }

  return () => new Promise(invoke);
}

function createRequest(method, body) {
  return {
    method,
    body,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
  };
}

const calls = {};

const apiRequest = ({ url, method, body }: ApiRequest): Promise<ApiResponse> => {
  let invocations = calls[method + url];

  if (!invocations) {
    invocations = {
      count: 0,
      time: Date.now(),
    };
    window.setTimeout(() => {
      delete calls[method + url];
    }, 3000);
  } else {
    invocations.count += 1;

    if (Date.now() - invocations.time <= 5000 && invocations.count >= 10) {
      // TODO: tweak after release to find the correct threshold, replace with throw once happy
      captureError({
        url,
        method,
        error: new Error('API Error: Exceded number of requests threshold of more than 5 in 5 seconds'),
        isWarning: true,
      });
    }
  }

  const fetchWithRetry = buildFetchWithRetry(url, createRequest(method, body));
  const rawResponse: Promise<any> = fetchWithRetry();

  const result = rawResponse.then((response: ApiResponse): ApiResponse => {
    if (!response) {
      throw new Error('API Error: No Response');
    }

    const statusCode = response.status;

    if (statusCode === UNAUTHORISED) {
      response.isRedirected = true;
      response.redirectLocation = config.urls.loginUrl;
    }

    // We could handle 3** codes here, but no need to right now.  3** will be reported as a warning

    return response;
  });

  // Handle side effects

  result.then(({ redirectLocation }: ApiResponse) => {
    if (redirectLocation) {
      window.location.assign(redirectLocation);
    }
  });

  rawResponse.then(({ status }: ApiResponse) => {
    if (status && status >= 300) {
      // All errors except BAD_REQUEST are considered warnings
      const isWarning = status !== BAD_REQUEST;
      captureError({ status, url, method, isWarning });
    }
  });

  rawResponse.catch((error) => {
    captureError({ url, method, error });
  });

  return result;
};

export default apiRequest;
